import React, { useState, useRef, useEffect } from 'react';

const GlassVisualizator = ({ glassDetails = [], selectedDetails = new Set(), onDetailClick }) => {
  const [formatsByGlassType, setFormatsByGlassType] = useState({});
  const [scale, setScale] = useState(1);

  // Доступные форматы листов
  const formats = {
    '3210x2550': { width: 3210, height: 2550, name: '3210×2550 8мм' },
    '3210x2250': { width: 3210, height: 2250, name: '3210×2250 8мм' },
    '2600x1800': { width: 2600, height: 1800, name: '2600×1800 4мм' }
  };

  // Функция получения формата для конкретного типа стекла
  const getFormatForGlassType = (glassType) => {
    const formatKey = formatsByGlassType[glassType] || '3210x2550';
    return formats[formatKey];
  };

  // Функция установки формата для типа стекла
  const setFormatForGlassType = (glassType, formatKey) => {
    setFormatsByGlassType(prev => ({
      ...prev,
      [glassType]: formatKey
    }));
  };

  // Генерация случайных цветов для разных типов стекла
  const getGlassTypeColor = (glassType) => {
    const colors = {
      'Прозрачный 8 мм': '#e3f2fd',
      'Прозрачный 6 мм': '#f3e5f5',
      'Прозрачный 4 мм': '#e8f5e8',
      'Бронза 8 мм': '#fff3e0',
      'Бронза 6 мм': '#fce4ec',
      'Бронза 4 мм': '#f1f8e9',
      'Графит 8 мм': '#f5f5f5',
      'Графит 6 мм': '#e0f2f1',
      'Графит 4 мм': '#ede7f6'
    };
    return colors[glassType] || '#f0f0f0';
  };

  // Группировка деталей по типам стекла
  const groupDetailsByGlassType = () => {
    const groups = {};
    glassDetails.forEach(detail => {
      const glassType = detail.glassType || 'Тип не указан';
      if (!groups[glassType]) {
        groups[glassType] = [];
      }
      groups[glassType].push(detail);
    });
    return groups;
  };

  // Алгоритм размещения деталей с учетом множественных листов для всех типов стекла
  const generateAllSheetsLayout = () => {
    const groups = groupDetailsByGlassType();
    const allGlassTypeSheets = [];
    
    // Обрабатываем каждый тип стекла отдельно
    Object.entries(groups).forEach(([glassType, details]) => {
      const currentFormat = getFormatForGlassType(glassType);
      const glassTypeSheets = [];
      let currentSheet = {
        glassType,
        format: currentFormat,
        details: [],
        sheetIndex: 0,
        currentX: 10,
        currentY: 10,
        rowHeight: 0
      };

      const margin = 5;
      const maxWidth = currentFormat.width - 20;
      const maxHeight = currentFormat.height - 20;

      details.forEach((detail, index) => {
        const width = detail.width || 100;
        const height = detail.height || 100;
        const quantity = detail.quantity || 1;
        
        // Размещаем детали по количеству
        for (let i = 0; i < quantity; i++) {
          // Проверяем, помещается ли деталь в текущую строку
          if (currentSheet.currentX + width > maxWidth) {
            // Переходим на новую строку
            currentSheet.currentX = 10;
            currentSheet.currentY += currentSheet.rowHeight + margin;
            currentSheet.rowHeight = 0;
          }

          // Проверяем, помещается ли деталь по высоте
          if (currentSheet.currentY + height > maxHeight) {
            // Создаем новый лист для этого типа стекла
            glassTypeSheets.push(currentSheet);
            currentSheet = {
              glassType,
              format: currentFormat,
              details: [],
              sheetIndex: glassTypeSheets.length,
              currentX: 10,
              currentY: 10,
              rowHeight: 0
            };
          }

          // Создаем уникальный ID для каждого экземпляра детали
          const instanceId = `${detail.id}_instance_${i}`;

          // Добавляем экземпляр детали на текущий лист
          currentSheet.details.push({
            ...detail,
            instanceId: instanceId,
            instanceNumber: i + 1,
            totalInstances: quantity,
            x: currentSheet.currentX,
            y: currentSheet.currentY,
            displayWidth: width,
            displayHeight: height,
            originalIndex: index,
            sheetIndex: currentSheet.sheetIndex
          });

          currentSheet.currentX += width + margin;
          currentSheet.rowHeight = Math.max(currentSheet.rowHeight, height);
        }
      });

      // Добавляем последний лист если в нем есть детали
      if (currentSheet.details.length > 0) {
        glassTypeSheets.push(currentSheet);
      }

      // Добавляем все листы этого типа стекла
      allGlassTypeSheets.push({
        glassType,
        sheets: glassTypeSheets,
        totalDetails: details.length
      });
    });

    return allGlassTypeSheets;
  };

  const glassTypeSheets = generateAllSheetsLayout();

  // Компонент для отрисовки одного листа
  const SheetCanvas = ({ sheet, glassType, sheetIndex, totalSheets }) => {
    const canvasRef = useRef(null);
    const [hoveredDetail, setHoveredDetail] = useState(null);

    const drawSheet = () => {
      const canvas = canvasRef.current;
      if (!canvas) return;

      const ctx = canvas.getContext('2d');
      const canvasWidth = 400;
      const canvasHeight = 300;
      const currentFormat = sheet.format;
      
      // Расчет масштаба для помещения формата в canvas
      const scaleX = (canvasWidth - 40) / currentFormat.width;
      const scaleY = (canvasHeight - 40) / currentFormat.height;
      const autoScale = Math.min(scaleX, scaleY);
      
      const actualScale = scale * autoScale;

      // Очистка canvas
      ctx.clearRect(0, 0, canvasWidth, canvasHeight);

      // Рисуем фон листа
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(20, 20, currentFormat.width * actualScale, currentFormat.height * actualScale);
      
      // Рисуем границу листа
      ctx.strokeStyle = '#333333';
      ctx.lineWidth = 2;
      ctx.strokeRect(20, 20, currentFormat.width * actualScale, currentFormat.height * actualScale);

      // Информация о листе
      ctx.fillStyle = '#666666';
      ctx.font = '10px Arial';
      ctx.fillText(`${currentFormat.width}×${currentFormat.height} мм`, 25, 15);
      
      // Номер листа
      ctx.fillStyle = '#333333';
      ctx.font = 'bold 12px Arial';
      ctx.textAlign = 'right';
      ctx.fillText(`Лист ${sheetIndex + 1}`, canvasWidth - 25, 15);
      ctx.textAlign = 'left';

      // Рисуем детали листа
      sheet.details.forEach((detail) => {
        const x = 20 + detail.x * actualScale;
        const y = 20 + detail.y * actualScale;
        const width = detail.displayWidth * actualScale;
        const height = detail.displayHeight * actualScale;

        const isSelected = selectedDetails.has(detail.instanceId);

        // Цвет детали (базовый цвет типа стекла)
        ctx.fillStyle = getGlassTypeColor(detail.glassType);
        ctx.fillRect(x, y, width, height);

        // Границы детали - более четкие и темные
        ctx.strokeStyle = '#333333';
        ctx.lineWidth = 2;
        ctx.strokeRect(x, y, width, height);

        // Подсветка при наведении
        if (hoveredDetail && hoveredDetail.instanceId === detail.instanceId) {
          ctx.strokeStyle = '#FF6B35';
          ctx.lineWidth = 3;
          ctx.strokeRect(x - 1, y - 1, width + 2, height + 2);
        }

        // Отметка выбранности - яркая зеленая галочка
        if (isSelected) {
          // Полупрозрачный зеленый оверлей
          ctx.fillStyle = 'rgba(76, 175, 80, 0.4)';
          ctx.fillRect(x, y, width, height);
          
          // Толстая зеленая рамка
          ctx.strokeStyle = '#4CAF50';
          ctx.lineWidth = 3;
          ctx.strokeRect(x + 1, y + 1, width - 2, height - 2);
          
          // Большая белая галочка
          const checkSize = Math.min(width, height) * 0.25;
          const centerX = x + width / 2;
          const centerY = y + height / 2;
          
          ctx.strokeStyle = '#FFFFFF';
          ctx.lineWidth = Math.max(2, checkSize / 4);
          ctx.lineCap = 'round';
          ctx.lineJoin = 'round';
          
          ctx.beginPath();
          ctx.moveTo(centerX - checkSize/2, centerY);
          ctx.lineTo(centerX - checkSize/4, centerY + checkSize/2);
          ctx.lineTo(centerX + checkSize/2, centerY - checkSize/2);
          ctx.stroke();
        }

        // Улучшенная подпись детали
        if (width > 20 && height > 12) {
          const textX = x + width / 2;
          const textY = y + height / 2;
          
          // Показываем размеры и номер заказа
          const sizeText = `${detail.width}×${detail.height}`;
          const orderText = `№${detail.orderNumber}`;
          const instanceText = detail.totalInstances > 1 ? `(${detail.instanceNumber}/${detail.totalInstances})` : '';
          
          // Адаптивный размер шрифта
          const baseFontSize = Math.max(8, Math.min(14, width / 12, height / 8));
          const smallFontSize = Math.max(6, baseFontSize - 2);
          
          // Более контрастный фон для текста
          const maxTextWidth = Math.max(
            ctx.measureText(sizeText).width,
            ctx.measureText(orderText).width
          );
          const textBgWidth = maxTextWidth + 8;
          const textBgHeight = instanceText ? 20 : 16;
          
          // Белый фон с тенью для лучшей читаемости
          ctx.fillStyle = 'rgba(0, 0, 0, 0.3)';
          ctx.fillRect(textX - textBgWidth/2 + 1, textY - textBgHeight/2 + 1, textBgWidth, textBgHeight);
          
          ctx.fillStyle = 'rgba(255, 255, 255, 0.95)';
          ctx.fillRect(textX - textBgWidth/2, textY - textBgHeight/2, textBgWidth, textBgHeight);
          
          ctx.textAlign = 'center';
          ctx.textBaseline = 'middle';
          
          // Размеры (крупнее и контрастнее)
          ctx.fillStyle = '#000000';
          ctx.font = `bold ${baseFontSize}px Arial`;
          ctx.fillText(sizeText, textX, instanceText ? textY - 4 : textY - 2);
          
          // Номер заказа (ярче синий)
          ctx.fillStyle = '#0052cc';
          ctx.font = `bold ${baseFontSize}px Arial`;
          ctx.fillText(orderText, textX, instanceText ? textY + 4 : textY + 6);
          
          // Номер экземпляра (если больше одного)
          if (instanceText && width > 30) {
            ctx.fillStyle = '#444444';
            ctx.font = `${smallFontSize}px Arial`;
            ctx.fillText(instanceText, textX, textY + 12);
          }
        }
      });

      // Статистика листа
      const sheetDetails = sheet.details.length;
      const selectedOnSheet = sheet.details.filter(d => selectedDetails.has(d.instanceId)).length;
      
      ctx.fillStyle = '#333333';
      ctx.font = '10px Arial';
      ctx.textAlign = 'left';
      ctx.fillText(`Деталей: ${sheetDetails} | Выбрано: ${selectedOnSheet}`, 25, canvasHeight - 10);
    };

    const handleCanvasClick = (event) => {
      const canvas = canvasRef.current;
      if (!canvas) return;

      // Останавливаем распространение события
      event.preventDefault();
      event.stopPropagation();

      const rect = canvas.getBoundingClientRect();
      const clickX = event.clientX - rect.left;
      const clickY = event.clientY - rect.top;

      const canvasWidth = 400;
      const canvasHeight = 300;
      const currentFormat = sheet.format;
      const scaleX = (canvasWidth - 40) / currentFormat.width;
      const scaleY = (canvasHeight - 40) / currentFormat.height;
      const autoScale = Math.min(scaleX, scaleY);
      const actualScale = scale * autoScale;

      // Сортируем детали по позиции (сначала по Y, потом по X) для более предсказуемого выбора
      const sortedDetails = [...sheet.details].sort((a, b) => {
        if (Math.abs(a.y - b.y) < 5) { // Если детали на одной линии (разница по Y < 5px)
          return a.x - b.x; // Сортируем по X (слева направо)
        }
        return a.y - b.y; // Сортируем по Y (сверху вниз)
      });

      let foundDetail = null;
      let foundDetails = []; // Собираем все детали под курсором

      // Проверяем клик по каждой детали листа
      for (const detail of sortedDetails) {
        const x = 20 + detail.x * actualScale;
        const y = 20 + detail.y * actualScale;
        const width = detail.displayWidth * actualScale;
        const height = detail.displayHeight * actualScale;

        // Проверяем попадание в границы детали с небольшим отступом для точности
        const margin = 2; // Уменьшаем область клика на 2px с каждой стороны
        if (clickX >= x + margin && clickX <= x + width - margin && 
            clickY >= y + margin && clickY <= y + height - margin) {
          
          foundDetails.push({
            detail,
            distance: Math.sqrt(
              Math.pow(clickX - (x + width/2), 2) + 
              Math.pow(clickY - (y + height/2), 2)
            )
          });
        }
      }

      // Если найдено несколько деталей, выбираем ближайшую к центру клика
      if (foundDetails.length > 0) {
        foundDetails.sort((a, b) => a.distance - b.distance);
        foundDetail = foundDetails[0].detail;
      }

      if (foundDetail) {
        console.log('Клик по детали:', {
          instanceId: foundDetail.instanceId,
          detailId: foundDetail.id,
          orderNumber: foundDetail.orderNumber,
          position: { x: foundDetail.x, y: foundDetail.y },
          size: { width: foundDetail.displayWidth, height: foundDetail.displayHeight },
          clickPosition: { clickX, clickY },
          foundDetailsCount: foundDetails.length
        });

        if (onDetailClick) {
          onDetailClick({
            ...foundDetail,
            clickedInstanceId: foundDetail.instanceId,
            isInstanceClick: true
          });
        }
      } else {
        console.log('Клик вне деталей:', { 
          clickX, 
          clickY,
          totalDetails: sheet.details.length,
          detailsUnderCursor: foundDetails.length
        });
      }
    };

    const handleCanvasMouseMove = (event) => {
      const canvas = canvasRef.current;
      if (!canvas) return;

      const rect = canvas.getBoundingClientRect();
      const mouseX = event.clientX - rect.left;
      const mouseY = event.clientY - rect.top;

      const canvasWidth = 400;
      const canvasHeight = 300;
      const currentFormat = sheet.format;
      const scaleX = (canvasWidth - 40) / currentFormat.width;
      const scaleY = (canvasHeight - 40) / currentFormat.height;
      const autoScale = Math.min(scaleX, scaleY);
      const actualScale = scale * autoScale;

      let foundHoverDetail = null;

      // Проверяем наведение на детали
      for (const detail of sheet.details) {
        const x = 20 + detail.x * actualScale;
        const y = 20 + detail.y * actualScale;
        const width = detail.displayWidth * actualScale;
        const height = detail.displayHeight * actualScale;

        if (mouseX >= x && mouseX <= x + width && 
            mouseY >= y && mouseY <= y + height) {
          foundHoverDetail = detail;
          break;
        }
      }

      if (foundHoverDetail !== hoveredDetail) {
        setHoveredDetail(foundHoverDetail);
      }
    };

    const handleCanvasMouseLeave = () => {
      setHoveredDetail(null);
    };

    useEffect(() => {
      drawSheet();
    }, [sheet, selectedDetails, scale, hoveredDetail]);

    return (
      <div className="border border-gray-300 rounded-lg p-2">
        <canvas
          ref={canvasRef}
          width={400}
          height={300}
          onClick={handleCanvasClick}
          onMouseMove={handleCanvasMouseMove}
          onMouseLeave={handleCanvasMouseLeave}
          className="border border-gray-200 rounded cursor-pointer"
          style={{ maxWidth: '100%' }}
        />
      </div>
    );
  };


  return (
    <div className="bg-white border border-gray-300 rounded-lg p-3">
      {/* Панель управления */}
      <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
        <h3 className="text-base font-medium">Визуализатор деталировки</h3>

        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-600">Масштаб:</span>
          <input
            type="range"
            min="0.5"
            max="2"
            step="0.1"
            value={scale}
            onChange={(e) => setScale(parseFloat(e.target.value))}
            className="w-20"
          />
          <span className="text-sm text-gray-600 w-8">{Math.round(scale * 100)}%</span>
        </div>
      </div>

      {/* Общая статистика */}
      <div className="bg-gray-50 p-3 rounded-lg mb-4">
        <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-gray-700">
          <span><span className="font-medium">Деталей:</span> {glassTypeSheets.reduce((sum, gts) => sum + gts.sheets.reduce((sheetSum, sheet) => sheetSum + sheet.details.length, 0), 0)}</span>
          <span><span className="font-medium">Выбрано:</span> {glassTypeSheets.reduce((sum, gts) => sum + gts.sheets.reduce((sheetSum, sheet) => sheetSum + sheet.details.filter(d => selectedDetails.has(d.instanceId)).length, 0), 0)}</span>
          <span><span className="font-medium">Типов:</span> {glassTypeSheets.length}</span>
          <span><span className="font-medium">Листов:</span> {glassTypeSheets.reduce((sum, gts) => sum + gts.sheets.length, 0)}</span>
        </div>
      </div>

      {/* Отображение всех типов стекла и их листов */}
      <div className="space-y-8">
        {glassTypeSheets.map((glassTypeData) => (
          <div key={glassTypeData.glassType} className="border border-gray-200 rounded-lg p-3">
            {/* Заголовок типа стекла */}
            <div className="flex flex-wrap items-start gap-2 mb-3">
              <div className="flex items-center gap-2 flex-1 min-w-0">
                <div
                  className="w-5 h-5 flex-shrink-0 border border-gray-400 rounded"
                  style={{ backgroundColor: getGlassTypeColor(glassTypeData.glassType) }}
                ></div>
                <h4 className="text-sm font-medium truncate">{glassTypeData.glassType}</h4>
              </div>

              <div className="flex flex-wrap items-center gap-2">
                {/* Селектор формата */}
                <div className="flex items-center gap-1">
                  <span className="text-xs text-gray-600 whitespace-nowrap">Формат:</span>
                  <select
                    value={formatsByGlassType[glassTypeData.glassType] || '3210x2550'}
                    onChange={(e) => setFormatForGlassType(glassTypeData.glassType, e.target.value)}
                    className="px-1 py-0.5 border border-gray-300 rounded text-xs"
                  >
                    {Object.entries(formats).map(([key, format]) => (
                      <option key={key} value={key}>{format.name}</option>
                    ))}
                  </select>
                </div>

                <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-xs text-gray-600">
                  <span><span className="font-medium">Дет:</span> {glassTypeData.sheets.reduce((sum, sheet) => sum + sheet.details.length, 0)}</span>
                  <span><span className="font-medium">Листов:</span> {glassTypeData.sheets.length}</span>
                  <span><span className="font-medium">Выбрано:</span> {glassTypeData.sheets.reduce((sum, sheet) => sum + sheet.details.filter(d => selectedDetails.has(d.instanceId)).length, 0)}</span>
                </div>
              </div>
            </div>

            {/* Листы этого типа стекла */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {glassTypeData.sheets.map((sheet, sheetIndex) => (
                <div key={sheetIndex} className="space-y-2">
                  <div className="text-sm font-medium text-gray-700 text-center">
                    Лист {sheetIndex + 1} из {glassTypeData.sheets.length}
                  </div>
                  <SheetCanvas
                    sheet={sheet}
                    glassType={glassTypeData.glassType}
                    sheetIndex={sheetIndex}
                    totalSheets={glassTypeData.sheets.length}
                  />
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* Легенда */}
      <div className="mt-6 p-4 bg-gray-50 rounded-lg">
        <h4 className="font-medium text-gray-800 mb-3">Обозначения:</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
          <div className="flex items-center space-x-3">
            <div className="w-8 h-6 bg-blue-100 border border-gray-400 rounded flex items-center justify-center text-xs">
              300×200<br/>№123
            </div>
            <span>Обычная деталь (размер × номер заказа)</span>
          </div>
          <div className="flex items-center space-x-3">
            <div className="w-8 h-6 bg-green-400 border-2 border-green-600 rounded flex items-center justify-center">
              <div className="text-white text-xs font-bold">✓</div>
            </div>
            <span><strong>Выбранная деталь</strong> (зеленая с галочкой)</span>
          </div>
        </div>
        <div className="mt-3 text-xs text-gray-600 bg-blue-50 p-2 rounded">
          <strong>Как использовать:</strong> Кликните по любой детали для выбора/отмены. Каждый экземпляр выбирается отдельно.
        </div>
      </div>

      {/* Пустое состояние */}
      {glassTypeSheets.length === 0 && (
        <div className="text-center py-12 text-gray-500">
          <div className="text-lg font-medium mb-2">Нет деталей для визуализации</div>
          <div className="text-sm">Детали появятся после загрузки деталировки заказов</div>
        </div>
      )}
    </div>
  );
};

export default GlassVisualizator;